import { omit } from 'lodash-es';
import { filter, map, pipe } from 'remeda';

import { Element } from '@@editor/helpers';
import { isUnityElement, type UnityElement } from '@@editor/typings/UnityElements';
import isState from '@@editor/utils/isState';
import makeState from '@@editor/utils/makeState';
import { cleanArray } from '@@utils/arrayUtils';

import { default as isSerializedState } from './isSerializedState';
import rules from './rules';
import { type ElementRule, type MarkRule, type TextRule } from './types';

type DeserializeNodeArgument =
    | Parameters<ElementRule<never, never>['deserialize']>[0]
    | Parameters<TextRule['deserialize']>[0]
    | Parameters<MarkRule['deserialize']>[0];

type SerializeNodeArgument =
    | Parameters<ElementRule<never, never>['serialize']>[0]
    | Parameters<TextRule['serialize']>[0]
    | Parameters<MarkRule['serialize']>[0];

class UnitySerializer {
    static isSerializedState = isSerializedState;

    deserializeNode = (node: DeserializeNodeArgument) => {
        if (node == null) {
            return node;
        }

        for (const rule of rules) {
            const res = rule.deserialize(omit(node, 'elementId'), this.deserializeNode);

            if (res != null) {
                if (Element.isBlockElement(res) && 'elementId' in node) {
                    res.id = node.elementId;
                }

                return res;
            }
        }
    };

    // `customRules` is only used in tests
    serializeNode = (node: SerializeNodeArgument, customRules?: any[]) => {
        if (node == null) {
            return node;
        }

        const r = Array.isArray(customRules) ? customRules : rules;

        for (const rule of r) {
            const res = rule.serialize(omit(node, 'id'), this.serializeNode);

            if (res != null) {
                if (isUnityElement(res) && 'id' in node) {
                    res.elementId = node.id;
                }

                return res;
            }
        }
    };

    deserialize(serializedState: UnityElement[]) {
        if (!Array.isArray(serializedState)) {
            return makeState();
        }

        const deserializedNodes = pipe(
            serializedState,
            // TODO: Due to null values in editor state, we just remove them for now (should be
            // removed alongside CD2-3674)
            cleanArray,
            map(this.deserializeNode.bind(this)),
            filter(Element.isElement),
            makeState,
        );

        return deserializedNodes;
    }

    serialize(value: any) {
        if (!isState(value)) {
            return [];
        }

        const serializedNodes = pipe(
            value,
            map((first) => this.serializeNode(first)),
            filter(isUnityElement),
        );

        return serializedNodes;
    }
}

export default UnitySerializer;
